import React from 'react'

import { Thumbnail } from 'features/views/attributes/Thumbnail'

import { Box } from 'ui/components/Box'
import { Input } from 'ui/components/Input'
import { InputEndActionSearch } from 'ui/components/Input/InputEndActionSearch'
import { Skeleton } from 'ui/components/Skeleton'
import { Tag } from 'ui/components/Tag'
import { Body, Headline } from 'ui/components/Text'

import { useAppTemplateCollectionItemState } from './hooks/useAppTemplateCollectionItemState'
import { useAppTemplateListState } from './hooks/useAppTemplateListState'
import { AppTemplateIcon } from './AppTemplateIcon'
import { AppTemplate } from './types'

import {
    AppTemplateCollectionItemEyebrowStyle,
    AppTemplateCollectionItemStyles,
    AppTemplateCollectionItemThumbnailStyle,
    AppTemplateCollectionItemThumbnailWrapperStyle,
    AppTemplateCollectionStyles,
    AppTemplateListStyle,
} from './AppTemplateList.css'

type AppTemplateListProps = {
    templates?: AppTemplate[]
    onSelectTemplate: (templateId: string) => void
    isLoading?: boolean
    isError?: boolean
}

export const AppTemplateList: React.FC<AppTemplateListProps> = ({
    templates: providedTemplates,
    onSelectTemplate,
    isLoading,
    isError,
}) => {
    const { templates, onSearchQueryChange, isEmpty, isEmptySearch } = useAppTemplateListState({
        templates: providedTemplates,
        isLoading,
        isError,
    })

    return (
        <Box flex column gap="xl" className={AppTemplateListStyle}>
            <Input
                autoFocus
                placeholder="Search for templates by use case or keywords..."
                endAction={(endActionProps) => (
                    <InputEndActionSearch {...endActionProps} pointerEvents="none" />
                )}
                disabled={isLoading || isError}
                onChange={onSearchQueryChange}
            />
            <Box overflowY="auto" height="full">
                {isError && (
                    <Box flex column gap="xl" className={AppTemplateListStyle}>
                        <Body color="textError" size="m">
                            There was a problem fetching templates
                        </Body>
                    </Box>
                )}
                {isEmpty && (
                    <Box flex column gap="xl" className={AppTemplateListStyle}>
                        <Body color="textHelper" size="m">
                            No templates found
                        </Body>
                    </Box>
                )}
                {isEmptySearch && (
                    <Box flex column gap="xl" className={AppTemplateListStyle}>
                        <Body color="textHelper" size="m">
                            No templates found for your query
                        </Body>
                    </Box>
                )}
                <AppTemplateCollection
                    templates={templates}
                    onSelectTemplate={onSelectTemplate}
                    isLoading={isLoading}
                />
            </Box>
        </Box>
    )
}

type AppTemplateCollectionProps = {
    templates: AppTemplate[]
    onSelectTemplate: (templateId: string) => void
    isLoading?: boolean
}

const AppTemplateCollection: React.FC<AppTemplateCollectionProps> = ({
    templates,
    onSelectTemplate,
    isLoading,
}) => {
    return (
        <Box className={AppTemplateCollectionStyles.styleFunction({})}>
            {templates?.map((t) => (
                <AppTemplateCollectionItem
                    key={t.id}
                    template={t}
                    onSelectTemplate={onSelectTemplate}
                    isLoading={isLoading}
                />
            ))}
        </Box>
    )
}

const DESCRIPTION_MAX_LINES = 2

type AppTemplateCollectionItemProps = {
    template: AppTemplate
    onSelectTemplate: (templateId: string) => void
    isLoading?: boolean
}

const AppTemplateCollectionItem: React.FC<AppTemplateCollectionItemProps> = ({
    template,
    onSelectTemplate,
    isLoading,
}) => {
    const { tags } = useAppTemplateCollectionItemState({ template })

    return (
        <Box
            role="button"
            className={AppTemplateCollectionItemStyles.styleFunction({ isLoading })}
            onClick={() => onSelectTemplate(template.id)}
        >
            <Box className={AppTemplateCollectionItemThumbnailWrapperStyle}>
                {!isLoading && (
                    <Box className={AppTemplateCollectionItemEyebrowStyle}>
                        <AppTemplateIcon size="s" template={template} />
                    </Box>
                )}
                <Skeleton isLoading={isLoading} display="block" width="full" height="full" noShrink>
                    <Thumbnail
                        src={template.thumbnail_url}
                        size="manual"
                        alt={template.name}
                        className={AppTemplateCollectionItemThumbnailStyle}
                    />
                </Skeleton>
            </Box>
            <Box py="s" pl="s" pr="5xl" flex column grow alignSelf="stretch">
                <Skeleton isLoading={isLoading}>
                    <Box trim maxWidth="full" minWidth={0}>
                        <Headline size="2xs">{template.name}</Headline>
                    </Box>
                </Skeleton>
                <Skeleton isLoading={isLoading} display="block" mt="xs">
                    <Box maxWidth="full" minWidth={0} grow alignSelf="stretch">
                        {template.description && (
                            <Body size="m" color="textWeak" maxLines={DESCRIPTION_MAX_LINES}>
                                {template.description}
                            </Body>
                        )}
                    </Box>
                </Skeleton>
            </Box>
            {tags.length > 0 && (
                <Box p="s" flex center wrap gap="xs" minWidth={0} maxWidth="full">
                    {tags.map((t) => (
                        <Skeleton key={t.label} isLoading={isLoading}>
                            <Tag
                                key={t.label}
                                size="2xs"
                                type="outline"
                                color={t.color}
                                pointerEvents="none"
                                maxWidth="full"
                            >
                                {t.label}
                            </Tag>
                        </Skeleton>
                    ))}
                </Box>
            )}
        </Box>
    )
}
